import { useEffect, useState } from 'react';
import { ERROR_CODES } from '@icebreaker/shared';
import { useLocale, useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import {
  OrgMark,
  Button,
  buttonClassName,
  EmptyState,
  ErrorBox,
  Seg,
  Skeleton,
  Table,
  Tag,
  useErrorMessage,
} from '@icebreaker/ui';
import {
  decideOrganizationVerification,
  fetchVerificationDocument,
  fetchVerificationQueue,
  mutationReset,
} from '../store/admin.slice';
import { ReasonDialog } from './reason-dialog';
import styles from './admin.module.css';

type QueueFilter = 'open' | 'decided';

/**
 * Kurum doğrulama karar kuyruğu (Story 18.4) — "kapının arkasında bir insan".
 *
 * Rol kapısı SUNUCUDADIR (`organization:verification_decide`); bu ekran yalnız
 * panel kabuğunun içinde yaşar ve kabuk `StaffGate`in ARKASINDADIR.
 *
 * TAŞIMA NOTU (panel 3. tur): web'deki `/admin/organization-verifications`
 * sayfasından BİREBİR geldi. Tek fark dilin kaynağıdır — web'de yol önekinden
 * (`/tr/...`) prop olarak iniyordu, panelde SSR yok: dil `useLocale()`ten
 * gelir (kullanıcının tercihi, adres değil).
 *
 * Belge bağlantısı YALNIZ TIKLANDIĞINDA istenir: her üretim denetim kaydına
 * (admin UUID + başvuru UUID) yazıldığı için listeyi çizerken üretilmez.
 * İstek SAGA üzerinden gider (CLAUDE.md: bileşen doğrudan API çağırmaz) ve
 * sonuç satırda İNDİRME bağlantısına dönüşür — sunucu bağlantıyı `attachment`
 * olarak imzalar, yani belge admin'in tarayıcısında çalıştırılmaz.
 */
export function VerificationQueueScreen() {
  const t = useTranslation();
  const { locale } = useLocale();
  const dispatch = useAppDispatch();
  const queue = useAppSelector((state: RootState) => state.admin.verificationQueue);
  const mutation = useAppSelector((state: RootState) => state.admin.mutation);
  const [filter, setFilter] = useState<QueueFilter>('open');
  const [rejectFor, setRejectFor] = useState<{ id: string; name: string } | null>(null);
  const documentState = useAppSelector((state: RootState) => state.admin.verificationDocument);
  const errorMessage = useErrorMessage();
  // İz yazılamadıysa belge bilerek verilmedi (C21): sebep söylenir, tekrar denenebilir.
  const documentError =
    documentState.phase !== 'failed'
      ? null
      : documentState.error?.code === ERROR_CODES.AUDIT_UNAVAILABLE
        ? errorMessage(documentState.error)
        : t.t('orgVerificationAdmin.documentError');

  useEffect(() => {
    dispatch(fetchVerificationQueue.request({ status: filter, page: 1, pageSize: 50 }));
  }, [dispatch, filter]);

  const dateFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <section className={styles.screen}>
      <div className={styles.titleRow}>
        <h1 className={styles.title}>{t.t('orgVerificationAdmin.title')}</h1>
        <span className={styles.titleMeta}>
          {t.t('orgVerificationAdmin.headline', { count: queue.total })}
        </span>
      </div>
      <p className={styles.lead}>{t.t('orgVerificationAdmin.lead')}</p>

      <div className={styles.toolbar}>
        <Seg<QueueFilter>
          ariaLabel={t.t('orgVerificationAdmin.title')}
          value={filter}
          onChange={setFilter}
          items={[
            { value: 'open', label: t.t('orgVerificationAdmin.segOpen') },
            { value: 'decided', label: t.t('orgVerificationAdmin.segDecided') },
          ]}
        />
      </div>

      {queue.error ? (
        <div className={styles.errorBox} role="alert">
          {t.t('orgVerificationAdmin.loadError')}
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              dispatch(fetchVerificationQueue.request({ status: filter, page: 1, pageSize: 50 }))
            }
          >
            {t.t('common.retry')}
          </Button>
        </div>
      ) : null}

      {documentError ? (
        <div className={styles.errorBox} role="alert">
          {documentError}
          {documentState.requestId ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                documentState.requestId &&
                dispatch(fetchVerificationDocument.request(documentState.requestId))
              }
            >
              {t.t('common.retry')}
            </Button>
          ) : null}
        </div>
      ) : null}

      <ErrorBox error={mutation.error} className={styles.errorBox} />

      {queue.phase === 'loading' && queue.items.length === 0 ? (
        <div className={styles.tableWrap}>
          <Skeleton label={t.t('a11y.loadingContent')} lines={3} lineHeight={48} />
        </div>
      ) : null}

      {queue.phase === 'succeeded' && queue.items.length === 0 ? (
        <div className={styles.tableWrap}>
          <EmptyState
            title={
              filter === 'open'
                ? t.t('orgVerificationAdmin.emptyTitle')
                : t.t('orgVerificationAdmin.emptyDecidedTitle')
            }
            body={t.t('orgVerificationAdmin.emptyBody')}
          />
        </div>
      ) : null}

      {queue.items.length > 0 ? (
        <div className={styles.tableWrap}>
          <Table>
            <thead>
              <tr>
                <th scope="col">{t.t('orgVerificationAdmin.colOrganization')}</th>
                <th scope="col">{t.t('orgVerificationAdmin.colDomain')}</th>
                <th scope="col">{t.t('orgVerificationAdmin.colSubmitted')}</th>
                <th scope="col">{t.t('orgVerificationAdmin.colWaiting')}</th>
                <th scope="col">{t.t('orgVerificationAdmin.colDocument')}</th>
                <th scope="col">{t.t('orgVerificationAdmin.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {queue.items.map((item) => (
                <tr key={item.requestId}>
                  <td>
                    <span className={styles.personCell}>
                      {/* Kurum bir VARLIKTIR: avatar köşeli (README 4.6). */}
                      <OrgMark
                        name={item.organizationName}
                        logoUrl={null}
                        fill={item.organizationBrandFill}
                        size="sm"
                      />
                      <span>
                        <span className={styles.personName}>{item.organizationName}</span>
                        <span className={styles.personMail}>
                          {item.submittedByName
                            ? t.t('orgVerificationAdmin.submittedBy', {
                                name: item.submittedByName,
                              })
                            : t.t('orgVerificationAdmin.submittedByUnknown')}
                        </span>
                      </span>
                    </span>
                  </td>
                  <td>{item.contactEmailDomain}</td>
                  <td className={styles.cellMeta}>
                    {dateFormat.format(new Date(item.submittedAt))}
                  </td>
                  <td className={styles.cellMeta}>
                    {t.t('orgVerificationAdmin.waitingHours', { count: item.waitingHours })}
                  </td>
                  {/*
                    Düğme standardı satır düğmelerine ✓/✕ ikonu getirdi (iki düğmede
                    +46 px); iki düğme sütunu `actionsCell` dar dolgusunu taşır ki
                    tablo 872 px orta kolonda yatay kaymasın (ölçüldü: 915 → 867).
                  */}
                  <td className={styles.actionsCell}>
                    {item.hasDocument ? (
                      documentState.requestId === item.requestId &&
                      documentState.phase === 'succeeded' &&
                      documentState.url ? (
                        /*
                          İNDİRME BAĞLANTISI, SEKME DEĞİL (QA 17C P3-3). İmzalı
                          GET `Content-Disposition: attachment` taşır; ama başlık
                          düşerse `target="_blank"` PDF'i sekmede açardı. `download`
                          niteliği ikinci savunmadır: aynı köken olmadığı için
                          tarayıcı dosya adını yok sayabilir, YÖNELİM yine indirmedir.
                        */
                        <a
                          className={buttonClassName('ghost', 'sm')}
                          href={documentState.url}
                          rel="noopener noreferrer"
                          download
                        >
                          {t.t('orgVerificationAdmin.documentDownload')}
                        </a>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          loading={
                            documentState.requestId === item.requestId &&
                            documentState.phase === 'loading'
                          }
                          loadingLabel={t.t('orgVerificationAdmin.documentPreparing')}
                          onClick={() =>
                            dispatch(fetchVerificationDocument.request(item.requestId))
                          }
                        >
                          {t.t('orgVerificationAdmin.documentView')}
                        </Button>
                      )
                    ) : (
                      <span className={styles.cellMeta}>
                        {t.t('orgVerificationAdmin.documentMissing')}
                      </span>
                    )}
                  </td>
                  <td className={styles.actionsCell}>
                    {filter === 'open' ? (
                      <span className={styles.rowActions}>
                        {/* Satırda tekrar eden karar: sıra Reddet, Doğrula (düğme standardı). */}
                        <Button
                          variant="rowNegative"
                          disabled={mutation.phase === 'loading'}
                          onClick={() => {
                            dispatch(mutationReset());
                            setRejectFor({ id: item.requestId, name: item.organizationName });
                          }}
                        >
                          {t.t('orgVerificationAdmin.reject')}
                        </Button>
                        <Button
                          variant="rowPositive"
                          disabled={mutation.phase === 'loading'}
                          onClick={() =>
                            dispatch(
                              decideOrganizationVerification.request({
                                requestId: item.requestId,
                                input: { decision: 'approve' },
                              }),
                            )
                          }
                        >
                          {t.t('orgVerificationAdmin.approve')}
                        </Button>
                      </span>
                    ) : (
                      <span className={styles.rowActions}>
                        <Tag variant={item.status === 'approved' ? 'success' : 'danger'}>
                          {item.status === 'approved'
                            ? t.t('orgVerificationAdmin.decidedApproved')
                            : t.t('orgVerificationAdmin.decidedRejected')}
                        </Tag>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : null}

      {/*
        BELGE İŞLEME NOTU — devir paketinin kurum tarafındaki sözü admin
        tarafında da yazılı durur ("Belge yalnızca doğrulama için kullanılır ve
        karara bağlandıktan 90 gün sonra silinir" + KVKK bloğu). Moderasyon
        ekranındaki `auditNote` ile aynı kutu deseni.
      */}
      <div className={styles.auditNote}>{t.t('orgVerificationAdmin.auditNote')}</div>

      {/* REDDE GEREKÇE ZORUNLU (Story 18.4) — başvurana AYNEN gösterilir. */}
      <ReasonDialog
        open={rejectFor !== null}
        title={t.t('orgVerificationAdmin.rejectTitle')}
        body={t.t('orgVerificationAdmin.rejectBody')}
        confirmLabel={t.t('orgVerificationAdmin.confirm')}
        tone="negative"
        loading={mutation.phase === 'loading'}
        onCancel={() => setRejectFor(null)}
        onConfirm={(reason) => {
          if (!rejectFor) return;
          dispatch(
            decideOrganizationVerification.request({
              requestId: rejectFor.id,
              input: { decision: 'reject', reason },
            }),
          );
          setRejectFor(null);
        }}
      />
    </section>
  );
}
